import { PaymentChannel } from "@/components/business/depositModal/api";
import { BaseResponse, request } from "@/lib/request";

export enum MemberLevel {
	MEMBER_LEVEL_UNSPECIFIED = "MEMBER_LEVEL_UNSPECIFIED",
	STANDARD = "STANDARD",
	PREMIUM = "PREMIUM",
	SUPREME = "SUPREME",
}

export enum UpdateUserProfileType {
	UPDATE_USER_PROFILE_TYPE_UNSPECIFIED = "UPDATE_USER_PROFILE_TYPE_UNSPECIFIED",
	// 普通模式，name和avatar必填
	UPDATE_USER_PROFILE_TYPE_NORMAL = "UPDATE_USER_PROFILE_TYPE_NORMAL",
	// 随机name
	UPDATE_USER_PROFILE_TYPE_ROLL_NAME = "UPDATE_USER_PROFILE_TYPE_ROLL_NAME",
	// 随机avatar
	UPDATE_USER_PROFILE_TYPE_ROLL_AVATAR = "UPDATE_USER_PROFILE_TYPE_ROLL_AVATAR",
}

export const updateProfile = async (data: {
	name: string;
	avatar: string;
	type: keyof typeof UpdateUserProfileType;
	showErrorToast?: boolean;
}): Promise<BaseResponse<null>> => {
	return await request.post(
		"/aifk/user/update_profile",
		data,
		{},
		data.showErrorToast ?? true,
	);
};

// 通用分页类型
export interface PageResponse<T> {
	total: number;
	list: T[];
}

export type OrderHistoryItem = {
	order_id: string;
	payment_channel: keyof typeof PaymentChannel;
	currency_name: string;
	currency_symbol: string;
	amount: number;
	gem: number;
	created_time: string;
};

// Gem来源
export enum GemSourceType {
	GEM_SOURCE_TYPE_UNSPECIFIED = "GEM_SOURCE_TYPE_UNSPECIFIED",
	RECHARGE = "RECHARGE", // 充值
	RECOMMENDATION_REWARD = "RECOMMENDATION_REWARD", // 推荐奖励
	CREATION_SHARING = "CREATION_SHARING", // 创作分成
	VISIT_CONSUMPTION = "VISIT_CONSUMPTION", // 游玩消耗
	NEW_USER_REWARD = "NEW_USER_REWARD", // 新用户奖励
}

export type GemHistoryItem = {
	amount: number;
	source_type: keyof typeof GemSourceType;
	creation_id: string;
	operation: string;
	created_time: string;
};

// 标签类型
export interface CreationTag {
	tag_id: string;
	name: string;
}

// 世界观词条类型
export interface WorldBookEntry {
	entry_name: string;
	keywords: string[];
	trigger_mode: string;
	keyword_mode: string;
	entry_content: string;
}
export enum CreationStatus {
	DRAFT = "DRAFT",
	PUBLISHED = "PUBLISHED",
	UNPUBLISHED = "UNPUBLISHED",
	DELETED = "DELETED",
}

export interface CreationItem {
	creation_id: string;
	title: string;
	tags: CreationTag[];
	brief_introduction: string;
	cover_image: string;
	anonymous: boolean;
	detailed_introduction: string;
	system_rule: string;
	user_identity: string;
	main_characters: string;
	npc: string;
	reply_format: string;
	reply_example: string;
	world_books: WorldBookEntry[];
	background_image: string;
	prologue: string;
	status: keyof typeof CreationStatus;
	popularity: number;
	view_count: number;
	paid_user_count: number;
	gem_consumption_count: number;
	score: number;
	author: string;
	edit_mode: string;
	created_time: string;
	updated_time: string;
	latest_edit_time: string;
	[key: string]: unknown;
}

export type CommentItem = {
	comment_id: string;
	creation_title: string;
	creation_id: string;
	content: string;
	likes: number;
	created_time: string;
};

export type OrderHistoryResponse = PageResponse<OrderHistoryItem>;
export type GemHistoryResponse = PageResponse<GemHistoryItem>;
export type CreationListResponse = PageResponse<CreationItem>;
export type CommentListResponse = PageResponse<CommentItem>;

export const orderHistory = async ({
	page_num = 1,
	page_size = 20,
	payment_channel,
}: {
	page_num: number;
	page_size: number;
	payment_channel: string;
}): Promise<BaseResponse<OrderHistoryResponse>> => {
	return await request.get("/aifk/order/history", {
		page_num,
		page_size,
		payment_channel,
	});
};

export const gemHistory = async ({
	page_num = 1,
	page_size = 20,
	source_type,
	creation_id,
}: {
	page_num: number;
	page_size: number;
	source_type: string;
	creation_id: string;
}): Promise<BaseResponse<GemHistoryResponse>> => {
	return await request.get("/aifk/gem/history", {
		page_num,
		page_size,
		source_type,
		creation_id,
	});
};

export const creationList = async ({
	page_num = 1,
	page_size = 20,
	status,
}: {
	page_num: number;
	page_size: number;
	status: keyof typeof CreationStatus;
}): Promise<BaseResponse<CreationListResponse>> => {
	return await request.get("/aifk/user/creation/list", {
		page_num,
		page_size,
		status,
	});
};

// /aifk/creation/delete

export const deleteCreation = async (
	creation_id: string,
): Promise<BaseResponse<null>> => {
	return await request.post("/aifk/creation/delete", {
		creation_id,
	});
};

export const unPublishCreation = async (
	creation_id: string,
): Promise<BaseResponse<null>> => {
	return await request.post("/aifk/creation/unpublish", {
		creation_id,
	});
};

export const publishCreation = async (
	creation_id: string,
): Promise<BaseResponse<null>> => {
	return await request.post("/aifk/creation/publish", {
		creation_id,
	});
};

export const commentList = async ({
	page_num = 1,
	page_size = 20,
}: {
	page_num: number;
	page_size: number;
}): Promise<BaseResponse<CommentListResponse>> => {
	return await request.get("/aifk/user/comment/list", {
		page_num,
		page_size,
	});
};

export const deleteComment = async (
	comment_id: string,
): Promise<BaseResponse<null>> => {
	return await request.post("/aifk/creation/comment/delete", { comment_id });
};

export const publishComment = async (
	creation_id: string,
	parent_comment_id: string,
	content: string,
): Promise<BaseResponse<null>> => {
	return await request.post("/aifk/creation/comment/publish", {
		creation_id,
		parent_comment_id,
		content,
	});
};

export const uploadImage = async (
	file: File,
	showErrorToast = true,
): Promise<BaseResponse<{ url: string }>> => {
	const formData = new FormData();
	formData.append("file", file);
	return request.post("/aifk/upload/image", formData, {}, showErrorToast);
};
